"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { featureFlags, FEATURE_FLAGS } from '@/lib/featureFlags';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Plus, Edit, Trash2, Award, MapPin, Globe, Calendar, RefreshCw, Eye, Linkedin } from 'lucide-react';
import { useVendorProfileEnhanced } from '@/hooks/useVendorProfileEnhanced';
import { useOrganizationProfile } from '@/hooks/useOrganizationProfile';
import { useToast } from '@/hooks/use-toast';
import { ReviewsSection } from '@/components/reviews/ReviewsSection';
import { useAuth } from '@/lib/auth';
import { publishPendingReviews } from '@/utils/publishReviews';
import { VendorSummaryModal } from '@/components/vendor/VendorSummaryModal';
import { useReviews } from '@/hooks/useReviews';
import { Review } from '@/lib/types/review';
import { VendorGalleryTab } from '@/components/vendor/VendorGalleryTab';
import { supabase } from '@/integrations/supabase/client';
import { CertificationSelector } from '@/components/vendor/CertificationSelector';
import { CapabilitySelector } from '@/components/vendor/CapabilitySelector';
import { CertificationDocumentUpload } from '@/components/vendor/CertificationDocumentUpload';
import { useCertificationDocuments } from '@/hooks/useCertificationDocuments';
import { useCertificationTypes } from '@/hooks/useCertificationTypes';
import { usePermissions } from '@/hooks/usePermissions';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield } from 'lucide-react';
import { RequiredDocumentsSection, checkAllDocumentsUploaded } from '@/components/vendor/RequiredDocumentsSection';
import { VendorLocationsTab } from '@/components/vendor/VendorLocationsTab';
import { useAddressValidation } from '@/hooks/useAddressValidation';
import { AddressValidationBadge } from '@/components/shared/AddressValidationBadge';
import { AddressSuggestionDialog } from '@/components/shared/AddressSuggestionDialog';
import { addressDiffersFromStandardized } from '@/utils/addressUtils';



const employeeCountRanges = [
  '1-10',
  '11-50',
  '51-200',
  '201-1000',
  '1000+'
];

const revenueRanges = [
  'Under $1M',
  '$1M-$5M',
  '$5M-$25M',
  '$25M-$100M',
  'Over $100M'
];

export default function VendorProfilePage() {
  const { user, organization } = useAuth();
  const {
    profile,
    certifications,
    capabilities,
    completion,
    pendingRequest,
    loading,
    createProfile,
    updateProfile,
    updateOrganizationName,
    addCertification,
    updateCertification,
    deleteCertification,
    addCapability,
    updateCapability,
    deleteCapability,
    requestVerification,
  } = useVendorProfileEnhanced();

  const {
    canManageVendorProfile,
    canManageVendorCertifications,
    canManageVendorCapabilities,
    canManageVendorGallery,
    canRequestVerification,
    isOwner,
  } = usePermissions();

  const { 
    profile: orgProfile, 
    loading: orgLoading, 
    createProfile: createOrgProfile, 
    updateProfile: updateOrgProfile 
  } = useOrganizationProfile();

  const { toast } = useToast();
  const { getUserReviewAggregate, getUserReviews } = useReviews();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam === 'company' ? 'company' : 'user');
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [reviewAggregate, setReviewAggregate] = useState(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [enhancedVerificationEnabled, setEnhancedVerificationEnabled] = useState(true);
  const [premiumVerificationEnabled, setPremiumVerificationEnabled] = useState(true);
  const [requireW9Flag, setRequireW9Flag] = useState(true);
  const [requireBusinessRegFlag, setRequireBusinessRegFlag] = useState(true);
  const [showSuggestionDialog, setShowSuggestionDialog] = useState(false);

  // Address validation
  const { validateAddress, isValidating, lastResult, clearValidation } = useAddressValidation();
  const [addressValidated, setAddressValidated] = useState(false);
  const [addressDeliverability, setAddressDeliverability] = useState<string | null>(null);

  // Check verification feature flags on mount
  useEffect(() => {
    const checkVerificationFlags = async () => {
      const [enhancedEnabled, premiumEnabled, w9Required, businessRegRequired] = await Promise.all([
        featureFlags.isEnabled(FEATURE_FLAGS.ENHANCED_VERIFICATION),
        featureFlags.isEnabled(FEATURE_FLAGS.PREMIUM_VERIFICATION),
        featureFlags.isEnabled(FEATURE_FLAGS.REQUIRE_W9),
        featureFlags.isEnabled(FEATURE_FLAGS.REQUIRE_BUSINESS_REGISTRATION),
      ]);
      setEnhancedVerificationEnabled(enhancedEnabled);
      setPremiumVerificationEnabled(premiumEnabled);
      setRequireW9Flag(w9Required);
      setRequireBusinessRegFlag(businessRegRequired);
    };
    checkVerificationFlags();
  }, []);
  const [userFormData, setUserFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    timezone: '',
    job_title: '',
    department: '',
    city: '',
    state: '',
    country: '',
  });

  const [orgNameData, setOrgNameData] = useState({
    organization_name: '',
  });

  const [formData, setFormData] = useState({
    founded_year: '',
    employee_count_range: '',
    annual_revenue_range: '',
    country: 'USA',
    state: '',
    street_address: '',
    city: '',
    zip_code: '',
    latitude: null as number | null,
    longitude: null as number | null,
    business_description: '',
    value_proposition: '',
    website_url: '',
    linkedin_url: '',
  });

  const [orgFormData, setOrgFormData] = useState({
    description: '',
    industry: '',
    company_size: '',
    headquarters_location: '',
    website_url: '',
    founded_year: null as number | null,
    annual_revenue_range: '',
    typical_project_types: [] as string[],
    contact_email: '',
    preferred_payment_terms: ''
  });

  const [certForm, setCertForm] = useState({
    certification_type_id: null as string | null,
    issuing_organization: '',
    certificate_number: '',
    issue_date: '',
    expiry_date: '',
  });

  const [certDocumentFile, setCertDocumentFile] = useState<File | null>(null);
  const { uploadCertificationDocument, uploading } = useCertificationDocuments();
  const { types } = useCertificationTypes();

  const [capabilityForm, setCapabilityForm] = useState({
    capability_id: null as string | null,
    business_percentage: 0,
  });
  const [businessPercentageInput, setBusinessPercentageInput] = useState<string>('');

  const handleUserProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (profile) {
        await updateProfile({
          first_name: userFormData.first_name,
          last_name: userFormData.last_name,
          phone_number: userFormData.phone_number,
          job_title: userFormData.job_title,
          department: userFormData.department,
          timezone: userFormData.timezone,
        });
      } else {
        await createProfile({
          first_name: userFormData.first_name,
          last_name: userFormData.last_name,
          phone_number: userFormData.phone_number,
          job_title: userFormData.job_title,
          department: userFormData.department,
          timezone: userFormData.timezone,
        });
      }
      setIsEditingUser(false);
    } catch (error) {
      console.error('Failed to save user profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleOrgNameUpdate = async () => {
    try {
      if (!orgNameData.organization_name.trim()) {
        toast({
          title: "Error",
          description: "Organization name cannot be empty",
          variant: "destructive",
        });
        return;
      }
      
      await updateOrganizationName(orgNameData.organization_name);
      window.location.reload(); // Reload to update header
    } catch (error) {
      console.error('Failed to update organization name:', error);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Validate address if we have address fields
      if (formData.street_address && formData.city && formData.state && formData.zip_code) {
        const result = await validateAddress({
          street: formData.street_address,
          city: formData.city,
          state: formData.state,
          zipcode: formData.zip_code,
        });

        // Check if standardized address differs from user input
        const addressDiffers = addressDiffersFromStandardized(
          {
            street: formData.street_address,
            city: formData.city,
            state: formData.state,
            zipcode: formData.zip_code,
          },
          result.standardizedAddress
        );

        const hasSuggestions = result.standardizedAddress || 
          (result.suggestions && result.suggestions.length > 0);

        // Show dialog if address was corrected OR if invalid with suggestions
        if (addressDiffers || (hasSuggestions && !result.isValid)) {
          setShowSuggestionDialog(true);
          setIsSubmitting(false);
          return;
        } else if (result.isValid) {
          // Address is valid and matches - save directly
          setAddressValidated(true);
          setAddressDeliverability(result.deliverability);
          await saveProfile(formData);
        } else {
          // No valid result, save anyway
          await saveProfile(formData);
        }
      } else {
        // No address to validate
        await saveProfile(formData);
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
    } finally {
      setTimeout(() => setIsSubmitting(false), 600);
    }
  };

  const saveProfile = async (data: typeof formData) => {
    const profileData = {
      ...data,
      country: 'USA', // Force country to 'USA' - platform is US-only
      founded_year: data.founded_year ? parseInt(data.founded_year) : null,
      latitude: null, // No longer geocoding
      longitude: null,
    };
    
    if (profile) {
      await updateProfile(profileData);
    } else {
      await createProfile(profileData);
    }
    
    setTimeout(() => {
      setIsEditing(false);
    }, 300);
  };

  const handleSelectSuggestedAddress = async (address: any) => {
    const updatedFormData = {
      ...formData,
      street_address: address.street,
      city: address.city,
      state: address.state,
      zip_code: address.zipcode,
    };
    setFormData(updatedFormData);
    setAddressValidated(true);
    setAddressDeliverability('deliverable');
    setShowSuggestionDialog(false);
    await saveProfile(updatedFormData);
    setIsSubmitting(false);
  };

  const handleKeepOriginalAddress = async () => {
    setShowSuggestionDialog(false);
    await saveProfile(formData);
    setIsSubmitting(false);
  };

  const handleManualValidation = async () => {
    if (formData.street_address && formData.city && formData.state && formData.zip_code) {
      const result = await validateAddress({
        street: formData.street_address,
        city: formData.city,
        state: formData.state,
        zipcode: formData.zip_code,
      });

      if (result.standardizedAddress) {
        // Check if address was corrected
        const addressDiffers = addressDiffersFromStandardized(
          {
            street: formData.street_address,
            city: formData.city,
            state: formData.state,
            zipcode: formData.zip_code,
          },
          result.standardizedAddress
        );

        if (addressDiffers || !result.isValid) {
          // Show dialog to let user choose
          setShowSuggestionDialog(true);
        } else {
          // Address matches exactly - just mark as validated
          setAddressValidated(true);
          setAddressDeliverability(result.deliverability);
        }
      } else if (result.suggestions && result.suggestions.length > 0) {
        setShowSuggestionDialog(true);
      }
    }
  };

  const handleAddressFieldChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setAddressValidated(false);
    setAddressDeliverability(null);
    clearValidation();
  };

  const handleAddCertification = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!certForm.certification_type_id || !certForm.issuing_organization) {
      toast({
        title: "Error",
        description: "Please select a certification and provide issuing organization",
        variant: "destructive",
      });
      return;
    }
    
    try {
      let documentUrl: string | null = null;

      // Upload document if provided
      if (certDocumentFile && profile) {
        documentUrl = await uploadCertificationDocument(
          certDocumentFile,
          profile.id
        );
      }

      await addCertification({
        ...certForm,
        issue_date: certForm.issue_date || null,
        expiry_date: certForm.expiry_date || null,
        document_url: documentUrl || undefined,
      });

      // Reset form
      setCertForm({
        certification_type_id: null,
        issuing_organization: '',
        certificate_number: '',
        issue_date: '',
        expiry_date: '',
      });
      setCertDocumentFile(null);
    } catch (error) {
      console.error('Failed to add certification:', error);
    }
  };

  const handleAddCapability = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!capabilityForm.capability_id) {
      toast({
        title: "Error",
        description: "Please select a capability",
        variant: "destructive",
      });
      return;
    }
    
    try {
      await addCapability({
        capability_id: capabilityForm.capability_id,
        business_percentage: capabilityForm.business_percentage,
      });
      setCapabilityForm({
        capability_id: null,
        business_percentage: 0,
      });
      setBusinessPercentageInput('');
    } catch (error) {
      console.error('Failed to add capability:', error);
    }
  };

  const handleBusinessPercentageChange = (value: string) => {
    setBusinessPercentageInput(value);
    const numValue = value === '' ? 0 : parseInt(value);
    setCapabilityForm(prev => ({ ...prev, business_percentage: Math.min(100, Math.max(0, numValue)) }));
  };

  const handleBusinessPercentageBlur = () => {
    const numValue = parseInt(businessPercentageInput);
    if (isNaN(numValue) || numValue < 0) {
      setBusinessPercentageInput('0');
      setCapabilityForm(prev => ({ ...prev, business_percentage: 0 }));
    } else if (numValue > 100) {
      setBusinessPercentageInput('100');
      setCapabilityForm(prev => ({ ...prev, business_percentage: 100 }));
    }
  };

  // Calculate total percentage for soft warning
  const totalBusinessPercentage = capabilities.reduce((sum, cap) => sum + (cap.business_percentage || 0), 0);

  // Load user profile data when available
  React.useEffect(() => {
    const loadUserData = async () => {
      if (profile) {
        setUserFormData({
          first_name: profile.first_name || '',
          last_name: profile.last_name || '',
          email: user?.email || '',
          phone_number: profile.phone_number || '',
          timezone: profile.timezone || '',
          job_title: profile.job_title || '',
          department: profile.department || '',
          city: '',
          state: '',
          country: '',
        });
      }
    };
    loadUserData();
  }, [profile, user]);

  // Load organization name when available
  React.useEffect(() => {
    if (organization?.name) {
      setOrgNameData({
        organization_name: organization.name,
      });
    }
  }, [organization]);

  // Load company profile data from vendor profile when available
  React.useEffect(() => {
    // Only update form data if we're not currently editing or submitting
    if (profile && !isEditing && !isSubmitting) {
      setFormData({
        founded_year: profile.founded_year?.toString() || '',
        employee_count_range: profile.employee_count_range || '',
        annual_revenue_range: profile.annual_revenue_range || '',
        country: profile.country || 'USA',
        state: profile.state || '',
        street_address: profile.street_address || '',
        city: profile.city || '',
        zip_code: profile.zip_code || '',
        latitude: profile.latitude,
        longitude: profile.longitude,
        business_description: profile.business_description || '',
        value_proposition: profile.value_proposition || '',
        website_url: profile.website_url || '',
        linkedin_url: profile.linkedin_url || '',
      });
    }
  }, [profile, isEditing, isSubmitting]);

  // Load organization profile data when available
  React.useEffect(() => {
    if (orgProfile) {
      setOrgFormData({
        description: orgProfile.description || '',
        industry: orgProfile.industry || '',
        company_size: orgProfile.company_size || '',
        headquarters_location: orgProfile.headquarters_location || '',
        website_url: orgProfile.website_url || '',
        founded_year: orgProfile.founded_year || null,
        annual_revenue_range: orgProfile.annual_revenue_range || '',
        typical_project_types: Array.isArray(orgProfile.typical_project_types) ? orgProfile.typical_project_types : [],
        contact_email: orgProfile.contact_email || '',
        preferred_payment_terms: orgProfile.preferred_payment_terms || ''
      });
    }
  }, [orgProfile]);

  // Load review aggregate and individual reviews data when user is available
  const userId = user?.id;
  React.useEffect(() => {
    let isCancelled = false;
    const loadReviewData = async () => {
      if (userId) {
        const [aggregate, userReviews] = await Promise.all([
          getUserReviewAggregate(userId),
          getUserReviews(userId)
        ]);
        if (!isCancelled) {
          setReviewAggregate(aggregate);
          setReviews(userReviews);
        }
      }
    };
    loadReviewData();
    return () => { isCancelled = true; };
  }, [userId, getUserReviewAggregate, getUserReviews]);

  if (loading || orgLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Vendor Profile{organization?.name ? ` - ${organization.name}` : ''}</h1>
          <p className="text-muted-foreground">
            Manage your business profile and showcase your capabilities
          </p>
        </div>
        {profile && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowSummaryModal(true)}
              className="flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              Vendor Summary
            </Button>
            <Badge variant={profile.is_verified ? "default" : "secondary"}>
              {profile.is_verified ? "Verified" : "Unverified"}
            </Badge>
            <Badge variant="outline">
              {profile.verification_level}
            </Badge>
          </div>
        )}
      </div>

      {/* Profile Completion Progress */}
      {completion && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Profile Completion
              <Badge variant="outline">{completion.overall_percentage}%</Badge>
            </CardTitle>
            <CardDescription>
              Complete your profile to improve visibility and trust
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Progress value={completion.overall_percentage} className="mb-4" />
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
              <div className={`flex items-center gap-2 ${completion.user_profile_complete ? 'text-success' : 'text-muted-foreground'}`}>
                <div className={`w-2 h-2 rounded-full ${completion.user_profile_complete ? 'bg-success' : 'bg-muted'}`} />
                User Profile
              </div>
              <div className={`flex items-center gap-2 ${completion.company_profile_complete ? 'text-success' : 'text-muted-foreground'}`}>
                <div className={`w-2 h-2 rounded-full ${completion.company_profile_complete ? 'bg-success' : 'bg-muted'}`} />
                Company Profile
              </div>
              <div className={`flex items-center gap-2 ${completion.certifications_complete ? 'text-success' : 'text-muted-foreground'}`}>
                <div className={`w-2 h-2 rounded-full ${completion.certifications_complete ? 'bg-success' : 'bg-muted'}`} />
                Certifications
              </div>
              <div className={`flex items-center gap-2 ${completion.capabilities_complete ? 'text-success' : 'text-muted-foreground'}`}>
                <div className={`w-2 h-2 rounded-full ${completion.capabilities_complete ? 'bg-success' : 'bg-muted'}`} />
                Capabilities
              </div>
              <div className={`flex items-center gap-2 ${completion.gallery_complete ? 'text-success' : 'text-muted-foreground'}`}>
                <div className={`w-2 h-2 rounded-full ${completion.gallery_complete ? 'bg-success' : 'bg-muted'}`} />
                Gallery
              </div>
              <div className={`flex items-center gap-2 ${completion.verification_complete ? 'text-success' : 'text-muted-foreground'}`}>
                <div className={`w-2 h-2 rounded-full ${completion.verification_complete ? 'bg-success' : 'bg-muted'}`} />
                Verification
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="user">User Profile</TabsTrigger>
          <TabsTrigger value="company">Company Profile</TabsTrigger>
        </TabsList>

        {/* User Profile Tab */}
        <TabsContent value="user">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Basic Information
                  </CardTitle>
                  <CardDescription>
                    Manage your personal profile information
                  </CardDescription>
                </div>
                {!isEditingUser && (
                  <Button
                    onClick={() => setIsEditingUser(true)}
                    className="flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handleUserProfileSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      value={userFormData.first_name}
                      onChange={(e) => setUserFormData(prev => ({ ...prev, first_name: e.target.value }))}
                      placeholder="John"
                      disabled={!isEditingUser}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      value={userFormData.last_name}
                      onChange={(e) => setUserFormData(prev => ({ ...prev, last_name: e.target.value }))}
                      placeholder="Doe"
                      disabled={!isEditingUser}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={userFormData.email}
                      placeholder="john.doe@example.com"
                      disabled
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      To change your email, visit{' '}
                      <a href="/settings/account" className="text-primary hover:underline font-medium">
                        Account Settings
                      </a>
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone_number">Phone Number *</Label>
                    <Input
                      id="phone_number"
                      type="tel"
                      value={userFormData.phone_number}
                      onChange={(e) => setUserFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                      placeholder="+1 (555) 123-4567"
                      disabled={!isEditingUser}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timezone">Timezone</Label>
                    <Select
                      value={userFormData.timezone}
                      onValueChange={(value) => setUserFormData(prev => ({ ...prev, timezone: value }))}
                      disabled={!isEditingUser}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select timezone" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EST">Eastern Time (EST)</SelectItem>
                        <SelectItem value="CST">Central Time (CST)</SelectItem>
                        <SelectItem value="MST">Mountain Time (MST)</SelectItem>
                        <SelectItem value="PST">Pacific Time (PST)</SelectItem>
                        <SelectItem value="UTC">UTC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="job_title">Job Title *</Label>
                    <Input
                      id="job_title"
                      value={userFormData.job_title}
                      onChange={(e) => setUserFormData(prev => ({ ...prev, job_title: e.target.value }))}
                      placeholder="Sales Manager"
                      disabled={!isEditingUser}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="department">Department</Label>
                    <Input
                      id="department"
                      value={userFormData.department}
                      onChange={(e) => setUserFormData(prev => ({ ...prev, department: e.target.value }))}
                      placeholder="Sales & Marketing"
                      disabled={!isEditingUser}
                    />
                  </div>
                </div>

                {isEditingUser && (
                  <div className="flex gap-4 pt-4">
                    <Button type="submit">
                      Update Profile
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsEditingUser(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Company Profile Tab */}
        <TabsContent value="company">
          {!isOwner && (
            <Alert className="mb-6">
              <Shield className="h-4 w-4" />
              <AlertDescription>
                Only organization owners can edit company profile information. You can view all details but cannot make changes.
              </AlertDescription>
            </Alert>
          )}
          
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-7">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="locations">Locations</TabsTrigger>
              <TabsTrigger value="certifications">Certifications</TabsTrigger>
              <TabsTrigger value="capabilities">Capabilities</TabsTrigger>
              <TabsTrigger value="gallery">Gallery</TabsTrigger>
              <TabsTrigger value="verification">Verification</TabsTrigger>
              <TabsTrigger value="reviews">Reviews</TabsTrigger>
            </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {!profile || isEditing ? (
            <Card>
              <CardHeader>
                <CardTitle>{profile ? 'Edit Profile' : 'Create Profile'}</CardTitle>
                <CardDescription>
                  {profile ? 'Update your business information' : 'Set up your vendor profile to start receiving opportunities'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-6 p-4 bg-muted rounded-lg">
                  <Label htmlFor="organization_name">Organization Name *</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="organization_name"
                      value={orgNameData.organization_name}
                      onChange={(e) => setOrgNameData({ organization_name: e.target.value })}
                      placeholder="Your organization name"
                      disabled={!canManageVendorProfile}
                      required
                    />
                    <Button 
                      type="button" 
                      onClick={handleOrgNameUpdate}
                      disabled={!canManageVendorProfile}
                    >
                      Update
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    This is your organization's legal or trade name. It will appear in the header and all correspondence.
                  </p>
                </div>

                <form onSubmit={handleProfileSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    <div className="space-y-2">
                      <Label htmlFor="founded_year">Founded Year *</Label>
                      <Input
                        id="founded_year"
                        type="number"
                        value={formData.founded_year}
                        onChange={(e) => setFormData(prev => ({ ...prev, founded_year: e.target.value }))}
                        placeholder="2020"
                        min="1900"
                        max={new Date().getFullYear()}
                        disabled={!canManageVendorProfile}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="employee_count">Employee Count *</Label>
                      <Select
                        value={formData.employee_count_range}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, employee_count_range: value }))}
                        disabled={!canManageVendorProfile}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select employee count" />
                        </SelectTrigger>
                        <SelectContent>
                          {employeeCountRanges.map((range) => (
                            <SelectItem key={range} value={range}>
                              {range}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="revenue_range">Annual Revenue Range</Label>
                      <Select
                        value={formData.annual_revenue_range}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, annual_revenue_range: value }))}
                        disabled={!canManageVendorProfile}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select revenue range" />
                        </SelectTrigger>
                        <SelectContent>
                          {revenueRanges.map((range) => (
                            <SelectItem key={range} value={range}>
                              {range}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="street_address">Street Address *</Label>
                      <Input
                        id="street_address"
                        value={formData.street_address}
                        onChange={(e) => setFormData(prev => ({ ...prev, street_address: e.target.value }))}
                        placeholder="123 Main Street"
                        disabled={!canManageVendorProfile}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                        placeholder="New York"
                        disabled={!canManageVendorProfile}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="state">State/Province *</Label>
                      <Select
                        value={formData.state}
                        onValueChange={(v) => setFormData(prev => ({ ...prev, state: v }))}
                        disabled={!canManageVendorProfile}
                      >
                        <SelectTrigger id="state">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          <SelectItem value="AL">Alabama (AL)</SelectItem>
                          <SelectItem value="AK">Alaska (AK)</SelectItem>
                          <SelectItem value="AZ">Arizona (AZ)</SelectItem>
                          <SelectItem value="AR">Arkansas (AR)</SelectItem>
                          <SelectItem value="CA">California (CA)</SelectItem>
                          <SelectItem value="CO">Colorado (CO)</SelectItem>
                          <SelectItem value="CT">Connecticut (CT)</SelectItem>
                          <SelectItem value="DE">Delaware (DE)</SelectItem>
                          <SelectItem value="FL">Florida (FL)</SelectItem>
                          <SelectItem value="GA">Georgia (GA)</SelectItem>
                          <SelectItem value="HI">Hawaii (HI)</SelectItem>
                          <SelectItem value="ID">Idaho (ID)</SelectItem>
                          <SelectItem value="IL">Illinois (IL)</SelectItem>
                          <SelectItem value="IN">Indiana (IN)</SelectItem>
                          <SelectItem value="IA">Iowa (IA)</SelectItem>
                          <SelectItem value="KS">Kansas (KS)</SelectItem>
                          <SelectItem value="KY">Kentucky (KY)</SelectItem>
                          <SelectItem value="LA">Louisiana (LA)</SelectItem>
                          <SelectItem value="ME">Maine (ME)</SelectItem>
                          <SelectItem value="MD">Maryland (MD)</SelectItem>
                          <SelectItem value="MA">Massachusetts (MA)</SelectItem>
                          <SelectItem value="MI">Michigan (MI)</SelectItem>
                          <SelectItem value="MN">Minnesota (MN)</SelectItem>
                          <SelectItem value="MS">Mississippi (MS)</SelectItem>
                          <SelectItem value="MO">Missouri (MO)</SelectItem>
                          <SelectItem value="MT">Montana (MT)</SelectItem>
                          <SelectItem value="NE">Nebraska (NE)</SelectItem>
                          <SelectItem value="NV">Nevada (NV)</SelectItem>
                          <SelectItem value="NH">New Hampshire (NH)</SelectItem>
                          <SelectItem value="NJ">New Jersey (NJ)</SelectItem>
                          <SelectItem value="NM">New Mexico (NM)</SelectItem>
                          <SelectItem value="NY">New York (NY)</SelectItem>
                          <SelectItem value="NC">North Carolina (NC)</SelectItem>
                          <SelectItem value="ND">North Dakota (ND)</SelectItem>
                          <SelectItem value="OH">Ohio (OH)</SelectItem>
                          <SelectItem value="OK">Oklahoma (OK)</SelectItem>
                          <SelectItem value="OR">Oregon (OR)</SelectItem>
                          <SelectItem value="PA">Pennsylvania (PA)</SelectItem>
                          <SelectItem value="RI">Rhode Island (RI)</SelectItem>
                          <SelectItem value="SC">South Carolina (SC)</SelectItem>
                          <SelectItem value="SD">South Dakota (SD)</SelectItem>
                          <SelectItem value="TN">Tennessee (TN)</SelectItem>
                          <SelectItem value="TX">Texas (TX)</SelectItem>
                          <SelectItem value="UT">Utah (UT)</SelectItem>
                          <SelectItem value="VT">Vermont (VT)</SelectItem>
                          <SelectItem value="VA">Virginia (VA)</SelectItem>
                          <SelectItem value="WA">Washington (WA)</SelectItem>
                          <SelectItem value="WV">West Virginia (WV)</SelectItem>
                          <SelectItem value="WI">Wisconsin (WI)</SelectItem>
                          <SelectItem value="WY">Wyoming (WY)</SelectItem>
                          <SelectItem value="DC">District of Columbia (DC)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="zip_code">Zip Code *</Label>
                      <Input
                        id="zip_code"
                        value={formData.zip_code}
                        onChange={(e) => setFormData(prev => ({ ...prev, zip_code: e.target.value }))}
                        placeholder="10001"
                        disabled={!canManageVendorProfile}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        value="United States"
                        disabled
                        className="bg-muted cursor-not-allowed"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="website_url">Website *</Label>
                      <Input
                        id="website_url"
                        type="text"
                        value={formData.website_url}
                        onChange={(e) => setFormData(prev => ({ ...prev, website_url: e.target.value }))}
                        placeholder="https://www.example.com"
                        disabled={!canManageVendorProfile}
                        required
                      />
                    </div>
                  </div>

                  {(formData.latitude && formData.longitude) && (
                    <div className="text-sm text-muted-foreground p-3 bg-muted rounded-md">
                      <MapPin className="h-4 w-4 inline mr-2" />
                      Coordinates: {formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="business_description">Business Description</Label>
                    <Textarea
                      id="business_description"
                      value={formData.business_description}
                      onChange={(e) => setFormData(prev => ({ ...prev, business_description: e.target.value }))}
                      placeholder="Describe your business, services, and what makes you unique..."
                      rows={4}
                      disabled={!canManageVendorProfile}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button type="submit" disabled={!canManageVendorProfile}>
                      {profile ? 'Update Profile' : 'Create Profile'}
                    </Button>
                    {profile && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setIsEditing(false)}
                        disabled={!canManageVendorProfile}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {organization?.name || 'Your Organization'}
                      {profile.is_verified && <Award className="h-5 w-5 text-yellow-500" />}
                    </CardTitle>
                    <CardDescription>
                      Founded {profile.founded_year}
                    </CardDescription>
                  </div>
                  {canManageVendorProfile && (
                    <Button onClick={() => {
                      setFormData({
                        founded_year: profile.founded_year?.toString() || '',
                        employee_count_range: profile.employee_count_range || '',
                        annual_revenue_range: profile.annual_revenue_range || '',
                        country: profile.country || 'USA',
                        state: profile.state || '',
                        street_address: profile.street_address || '',
                        city: profile.city || '',
                        zip_code: profile.zip_code || '',
                        latitude: profile.latitude || null,
                        longitude: profile.longitude || null,
                        business_description: profile.business_description || '',
                        value_proposition: profile.value_proposition || '',
                        website_url: profile.website_url || '',
                        linkedin_url: profile.linkedin_url || '',
                      });
                      setIsEditing(true);
                    }}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Business Information Section */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg">Business Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {profile.founded_year && (
                      <div>
                        <span className="text-sm text-muted-foreground">Founded Year</span>
                        <p className="text-sm font-medium">{profile.founded_year}</p>
                      </div>
                    )}
                    {profile.employee_count_range && (
                      <div>
                        <span className="text-sm text-muted-foreground">Employee Count</span>
                        <p className="text-sm font-medium">{profile.employee_count_range}</p>
                      </div>
                    )}
                    {profile.annual_revenue_range && (
                      <div>
                        <span className="text-sm text-muted-foreground">Annual Revenue</span>
                        <p className="text-sm font-medium">{profile.annual_revenue_range}</p>
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Contact & Location Section */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg">Contact & Location</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {profile.street_address && (
                      <div>
                        <span className="text-sm text-muted-foreground">Street Address</span>
                        <p className="text-sm font-medium">{profile.street_address}</p>
                      </div>
                    )}
                    {profile.city && (
                      <div>
                        <span className="text-sm text-muted-foreground">City</span>
                        <p className="text-sm font-medium">{profile.city}</p>
                      </div>
                    )}
                    {profile.state && (
                      <div>
                        <span className="text-sm text-muted-foreground">State/Province</span>
                        <p className="text-sm font-medium">{profile.state}</p>
                      </div>
                    )}
                    {profile.zip_code && (
                      <div>
                        <span className="text-sm text-muted-foreground">Zip Code</span>
                        <p className="text-sm font-medium">{profile.zip_code}</p>
                      </div>
                    )}
                    {profile.country && (
                      <div>
                        <span className="text-sm text-muted-foreground">Country</span>
                        <p className="text-sm font-medium">{profile.country}</p>
                      </div>
                    )}
                    {(profile.latitude && profile.longitude) && (
                      <div>
                        <span className="text-sm text-muted-foreground">Coordinates</span>
                        <p className="text-sm font-medium">{profile.latitude}, {profile.longitude}</p>
                      </div>
                    )}
                    {profile.website_url && (
                      <div>
                        <span className="text-sm text-muted-foreground">Website</span>
                        <a 
                          href={profile.website_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
                        >
                          <Globe className="h-3 w-3" />
                          {profile.website_url}
                        </a>
                      </div>
                    )}
                    {profile.linkedin_url && (
                      <div>
                        <span className="text-sm text-muted-foreground">LinkedIn</span>
                        <a 
                          href={profile.linkedin_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
                        >
                          <Linkedin className="h-3 w-3" />
                          LinkedIn Profile
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                {/* About the Company Section */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg">About the Company</h4>
                  {profile.business_description && (
                    <div>
                      <span className="text-sm text-muted-foreground">Business Description</span>
                      <p className="text-sm mt-1">{profile.business_description}</p>
                    </div>
                  )}
                  {profile.value_proposition && (
                    <div>
                      <span className="text-sm text-muted-foreground">Value Proposition</span>
                      <p className="text-sm mt-1">{profile.value_proposition}</p>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Performance Stats Section */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg">Performance Stats</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <div className="text-2xl font-bold text-primary">{profile.total_jobs_completed}</div>
                      <div className="text-sm text-muted-foreground">Jobs Completed</div>
                    </div>
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <div className="text-2xl font-bold text-primary">{(profile.average_rating ?? 0).toFixed(1)}</div>
                      <div className="text-sm text-muted-foreground">Average Rating</div>
                    </div>
                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                      <div className="text-2xl font-bold text-primary">{(profile.on_time_delivery_rate ?? 0).toFixed(0)}%</div>
                      <div className="text-sm text-muted-foreground">On-Time Delivery</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="locations" className="space-y-6">
          <VendorLocationsTab userId={user?.id || ''} canEdit={isOwner} />
        </TabsContent>

        <TabsContent value="certifications" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Add Certification</CardTitle>
                <CardDescription>
                  Add professional certifications to build trust with clients
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddCertification} className="space-y-4">
                  <CertificationSelector
                    selectedTypeId={certForm.certification_type_id}
                    onTypeSelect={(typeId) => setCertForm(prev => ({ ...prev, certification_type_id: typeId }))}
                    disabled={!canManageVendorCertifications}
                  />

                  <div className="space-y-2">
                    <Label htmlFor="issuing_org">Issuing Organization *</Label>
                    <Input
                      id="issuing_org"
                      value={certForm.issuing_organization}
                      onChange={(e) => setCertForm(prev => ({ ...prev, issuing_organization: e.target.value }))}
                      placeholder="e.g., International Organization for Standardization"
                      disabled={!canManageVendorCertifications}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cert_number">Certificate Number</Label>
                    <Input
                      id="cert_number"
                      value={certForm.certificate_number}
                      onChange={(e) => setCertForm(prev => ({ ...prev, certificate_number: e.target.value }))}
                      placeholder="Certificate number"
                      disabled={!canManageVendorCertifications}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="issue_date">Issue Date</Label>
                      <Input
                        id="issue_date"
                        type="date"
                        value={certForm.issue_date}
                        onChange={(e) => setCertForm(prev => ({ ...prev, issue_date: e.target.value }))}
                        disabled={!canManageVendorCertifications}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="expiry_date">Expiry Date</Label>
                      <Input
                        id="expiry_date"
                        type="date"
                        value={certForm.expiry_date}
                        onChange={(e) => setCertForm(prev => ({ ...prev, expiry_date: e.target.value }))}
                        disabled={!canManageVendorCertifications}
                      />
                    </div>
                  </div>

                  <CertificationDocumentUpload
                    onFileSelect={setCertDocumentFile}
                    onRemove={() => setCertDocumentFile(null)}
                    disabled={uploading || !canManageVendorCertifications}
                    required={(() => {
                      const selectedType = types.find(t => t.id === certForm.certification_type_id);
                      const requiredCategories = [
                        'Packaging Certifications',
                        'Quality Certifications',
                        'Diversity Certifications',
                        'Small Business Certifications'
                      ];
                      return selectedType?.category ? requiredCategories.includes(selectedType.category.name) : false;
                    })()}
                  />

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={uploading || !canManageVendorCertifications}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {uploading ? 'Uploading...' : 'Add Certification'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Your Certifications</CardTitle>
                <CardDescription>
                  {certifications.length} certification{certifications.length !== 1 ? 's' : ''} added
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {certifications.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      No certifications added yet
                    </p>
                  ) : (
                    certifications.map((cert) => (
                      <div key={cert.id} className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold">
                                {cert.certification_type?.name || cert.certification_name}
                              </h4>
                              {cert.certification_name && !cert.certification_type && (
                                <Badge variant="secondary" className="text-xs">Legacy</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{cert.issuing_organization}</p>
                            {cert.certificate_number && (
                              <p className="text-xs text-muted-foreground">#{cert.certificate_number}</p>
                            )}
                            {cert.expiry_date && (
                              <div className="flex items-center gap-1 mt-2">
                                <Calendar className="h-3 w-3" />
                                <span className="text-xs">Expires: {new Date(cert.expiry_date).toLocaleDateString()}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={cert.verification_status === 'verified' ? 'default' : 'secondary'}>
                              {cert.verification_status}
                            </Badge>
                            {canManageVendorCertifications && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => deleteCertification(cert.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {cert.document_url && (
                          <CertificationDocumentUpload
                            onFileSelect={() => {}}
                            existingDocumentUrl={cert.document_url}
                            disabled={true}
                          />
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="capabilities" className="space-y-6">
          {/* Percentage Warning */}
          {capabilities.length > 0 && totalBusinessPercentage !== 100 && (
            <Alert className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
              <AlertDescription className="text-yellow-700 dark:text-yellow-400">
                Your capability percentages sum to {totalBusinessPercentage}%. Consider adjusting to total 100%.
              </AlertDescription>
            </Alert>
          )}
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Add Capability</CardTitle>
                <CardDescription>
                  Indicate the percentage of your business that each capability represents
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddCapability} className="space-y-4">
                  <CapabilitySelector
                    selectedCapabilityId={capabilityForm.capability_id}
                    onCapabilitySelect={(capabilityId) => setCapabilityForm(prev => ({ ...prev, capability_id: capabilityId }))}
                    disabled={!canManageVendorCapabilities}
                  />

                  <div className="space-y-2">
                    <Label htmlFor="business_percentage">% of Business</Label>
                    <div className="relative">
                      <Input
                        id="business_percentage"
                        type="number"
                        value={businessPercentageInput}
                        onChange={(e) => handleBusinessPercentageChange(e.target.value)}
                        onBlur={handleBusinessPercentageBlur}
                        placeholder="0"
                        min="0"
                        max="100"
                        disabled={!canManageVendorCapabilities}
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      What percentage of your business does this capability represent?
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full"
                    disabled={!canManageVendorCapabilities}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Capability
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Your Capabilities</CardTitle>
                <CardDescription>
                  {capabilities.length} capabilit{capabilities.length !== 1 ? 'ies' : 'y'} listed
                  {capabilities.length > 0 && ` (${totalBusinessPercentage}% total)`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {capabilities.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">
                      No capabilities added yet
                    </p>
                  ) : (
                    capabilities.map((cap) => (
                      <div key={cap.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold">
                                {cap.capability?.name || 'Unknown Capability'}
                              </h4>
                              <Badge variant="secondary" className="text-xs">
                                {cap.business_percentage}%
                              </Badge>
                            </div>
                          </div>
                          {canManageVendorCapabilities && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteCapability(cap.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="gallery" className="space-y-6">
          {!canManageVendorGallery && (
            <Alert className="mb-6">
              <Shield className="h-4 w-4" />
              <AlertDescription>
                Only organization owners can manage the gallery. Contact your organization owner to upload or remove images.
              </AlertDescription>
            </Alert>
          )}
          {profile && (
            <VendorGalleryTab 
              profileId={profile.id}
              galleryImages={profile.gallery_images || []}
              canManage={canManageVendorGallery}
              onUpdate={async () => {
                // Refetch profile to get updated gallery
                const { data } = await supabase
                  .from('vendor_profiles_enhanced')
                  .select('*')
                  .eq('id', profile.id)
                  .single();
                if (data) {
                  // Update the profile in state
                  window.location.reload(); // Simple refresh to update state
                }
              }}
            />
          )}
        </TabsContent>

        <TabsContent value="verification" className="space-y-6">
          {/* Required Business Documents Section */}
          {profile && (
            <RequiredDocumentsSection
              vendorProfileId={profile.id}
              isVerified={profile.is_verified}
              documents={{
                w9_document_url: profile.w9_document_url || null,
                w9_uploaded_at: profile.w9_uploaded_at || null,
                business_registration_url: profile.business_registration_url || null,
                business_registration_uploaded_at: profile.business_registration_uploaded_at || null,
                cgl_insurance_url: profile.cgl_insurance_url || null,
                cgl_insurance_uploaded_at: profile.cgl_insurance_uploaded_at || null,
                cgl_insurance_expiry_date: profile.cgl_insurance_expiry_date || null,
              }}
              onDocumentChange={() => {
                // Refetch the profile to get updated document data
                window.location.reload();
              }}
              disabled={!canRequestVerification}
            />
          )}

          <Card>
            <CardHeader>
              <CardTitle>Profile Verification</CardTitle>
              <CardDescription>
                Increase trust and visibility by verifying your business profile
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Basic Verification</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 mb-4">
                    <li>• Business Registration</li>
                    <li>• Insurance Coverage</li>
                    <li>• Online Presence Review</li>
                  </ul>
                  {(() => {
                    const allDocsUploaded = profile ? checkAllDocumentsUploaded({
                      w9_document_url: profile.w9_document_url || null,
                      business_registration_url: profile.business_registration_url || null,
                      cgl_insurance_url: profile.cgl_insurance_url || null,
                    }, requireBusinessRegFlag, requireW9Flag) : false;
                    const isDisabled = !allDocsUploaded || !canRequestVerification || profile?.verification_level === 'basic' || profile?.verification_level === 'enhanced' || profile?.verification_level === 'premium' || (pendingRequest && pendingRequest.requested_level === 'basic');
                    return (
                      <div>
                        <Button 
                          size="sm" 
                          onClick={() => requestVerification('basic')}
                          disabled={isDisabled}
                        >
                          {profile?.verification_level === 'basic' 
                            ? 'Verified' 
                            : (pendingRequest && pendingRequest.requested_level === 'basic') 
                              ? 'Pending' 
                              : 'Request Basic'}
                        </Button>
                        {!allDocsUploaded && !profile?.is_verified && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Upload all required documents to enable
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Enhanced Verification</h4>
                  <Button
                    size="sm" 
                    variant={!enhancedVerificationEnabled ? "secondary" : "default"}
                    onClick={() => requestVerification('enhanced')}
                    disabled={!enhancedVerificationEnabled || !canRequestVerification || profile?.verification_level === 'enhanced' || profile?.verification_level === 'premium' || (pendingRequest && pendingRequest.requested_level === 'enhanced')}
                  >
                    {!enhancedVerificationEnabled
                      ? 'Coming Soon'
                      : profile?.verification_level === 'enhanced' 
                        ? 'Verified' 
                        : (pendingRequest && pendingRequest.requested_level === 'enhanced') 
                          ? 'Pending' 
                          : 'Request Enhanced'}
                  </Button>
                </div>

                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Premium Verification</h4>
                  <Button
                    size="sm" 
                    variant={!premiumVerificationEnabled ? "secondary" : "default"}
                    onClick={() => requestVerification('premium')}
                    disabled={!premiumVerificationEnabled || !canRequestVerification || profile?.verification_level === 'premium' || (pendingRequest && pendingRequest.requested_level === 'premium')}
                  >
                    {!premiumVerificationEnabled
                      ? 'Coming Soon'
                      : profile?.verification_level === 'premium' 
                        ? 'Verified' 
                        : (pendingRequest && pendingRequest.requested_level === 'premium') 
                          ? 'Pending' 
                          : 'Request Premium'}
                  </Button>
                </div>
              </div>

              {profile && (
                  <div className="bg-muted/50 rounded-lg p-4">
                    <h4 className="font-semibold mb-2">Current Status</h4>
                    <div className="flex items-center gap-2">
                      <Badge variant={profile.is_verified ? "default" : "secondary"}>
                        {profile.is_verified ? "Verified" : "Unverified"}
                      </Badge>
                      <Badge variant="outline">
                        {profile.verification_level}
                      </Badge>
                      {pendingRequest && (
                        <Badge variant="outline">
                          {pendingRequest.requested_level} pending
                        </Badge>
                      )}
                    </div>
                    {profile.verified_at && (
                      <p className="text-sm text-muted-foreground mt-2">
                        Verified on {new Date(profile.verified_at).toLocaleDateString()}
                      </p>
                    )}
                    {pendingRequest && (
                      <p className="text-sm text-muted-foreground mt-2">
                        Verification request submitted {new Date(pendingRequest.requested_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reviews Tab */}
        <TabsContent value="reviews">
          {user && (
            <ReviewsSection 
              userId={user.id} 
              userType="vendor" 
              showTitle={false}
              providedReviews={reviews}
              providedAggregate={reviewAggregate}
            />
          )}
        </TabsContent>
      </Tabs>
    </TabsContent>
  </Tabs>

      {/* Vendor Summary Modal */}
      <VendorSummaryModal
        isOpen={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        profile={profile}
        certifications={certifications}
        capabilities={capabilities}
        reviewAggregate={reviewAggregate}
        reviews={reviews}
      />

      {/* Address Suggestion Dialog - only render when lastResult exists */}
      {lastResult && (
        <AddressSuggestionDialog
          open={showSuggestionDialog}
          onOpenChange={setShowSuggestionDialog}
          originalAddress={{
            street: formData.street_address || '',
            city: formData.city || '',
            state: formData.state || '',
            zipcode: formData.zip_code || '',
          }}
          validationResult={lastResult}
          onSelectAddress={handleSelectSuggestedAddress}
          onKeepOriginal={handleKeepOriginalAddress}
        />
      )}
    </div>
  );
}